/**
 * Owns the authenticated Reader HTTP contracts. Source text is optional by
 * selection, while translation and engagement state remain explicit.
 */
import { z } from "zod";
import { canonicalIdSchema } from "./content.ts";

export const readerLanguageSchema = z.enum(["zh-CN", "en"]);
export const readerReadQuerySchema = z
  .object({
    displayLanguage: readerLanguageSchema.optional(),
    source: z.literal("full").optional(),
    sourceSegmentId: canonicalIdSchema.optional()
  })
  .strict()
  .refine(
    (query) =>
      !(query.source !== undefined && query.sourceSegmentId !== undefined),
    { message: "Choose full source or one source segment" }
  );
export const readerTranslationStatusSchema = z.enum([
  "source",
  "pending",
  "processing",
  "published",
  "failed",
  "outdated"
]);

const nullableTextSchema = z.string().nullable();

const readerProgressSchema = z
  .object({
    contentVersionId: canonicalIdSchema,
    segmentIndex: z.number().int().nonnegative(),
    completed: z.boolean()
  })
  .strict();

export const readerContentResponseSchema = z
  .object({
    content: z
      .object({
        id: canonicalIdSchema,
        kind: z.enum(["post", "work"]),
        status: z.enum(["published", "hidden"]),
        author: z
          .object({
            id: canonicalIdSchema,
            username: z.string().min(1),
            displayName: z.string().min(1)
          })
          .strict()
      })
      .strict(),
    sourceVersion: z
      .object({
        id: canonicalIdSchema,
        versionNumber: z.number().int().positive(),
        language: z.literal("zh-CN"),
        title: z.string().min(1),
        fullSource: nullableTextSchema
      })
      .strict(),
    display: z
      .object({
        language: readerLanguageSchema,
        translationStatus: readerTranslationStatusSchema,
        aiTranslated: z.boolean(),
        provider: nullableTextSchema,
        model: nullableTextSchema,
        title: nullableTextSchema.optional(),
        summary: nullableTextSchema.optional(),
        feedExcerpt: nullableTextSchema.optional(),
        titleAnnotations: z
          .array(
            z
              .object({
                sourceTerm: z.string().min(1),
                occurrenceIndex: z.number().int().nonnegative(),
                annotation: z.string().min(1),
                spoiler: z.boolean()
              })
              .strict()
          )
          .optional(),
        sourceRevealAvailable: z.boolean(),
        segments: z
          .array(
            z
              .object({
                sourceSegmentId: canonicalIdSchema,
                segmentIndex: z.number().int().nonnegative(),
                displayText: nullableTextSchema,
                sourceText: nullableTextSchema,
                annotations: z
                  .array(
                    z
                      .object({
                        sourceTerm: z.string().min(1),
                        occurrenceIndex: z.number().int().nonnegative(),
                        annotation: z.string().min(1),
                        spoiler: z.boolean()
                      })
                      .strict()
                  )
                  .optional()
              })
              .strict()
          )
          .min(1)
      })
      .strict(),
    canonicalEntities: z
      .object({
        fandomIds: z.array(canonicalIdSchema),
        characterIds: z.array(canonicalIdSchema),
        shipIds: z.array(canonicalIdSchema),
        tagIds: z.array(canonicalIdSchema)
      })
      .strict(),
    engagement: z
      .object({
        bookmarked: z.boolean(),
        reaction: z.literal("kudos").nullable(),
        readingProgress: readerProgressSchema.nullable()
      })
      .strict()
  })
  .strict();

export const readerBookmarkMutationResponseSchema = z
  .object({
    contentId: canonicalIdSchema,
    bookmarked: z.boolean()
  })
  .strict();

export const readerReactionMutationResponseSchema = z
  .object({
    contentId: canonicalIdSchema,
    reaction: z.literal("kudos").nullable()
  })
  .strict();

export const readerProgressMutationRequestSchema = z
  .object({
    contentVersionId: canonicalIdSchema,
    segmentIndex: z.number().int().nonnegative(),
    completed: z.boolean()
  })
  .strict();

export const readerProgressMutationResponseSchema = z
  .object({
    contentId: canonicalIdSchema,
    contentVersionId: canonicalIdSchema,
    segmentIndex: z.number().int().nonnegative(),
    completed: z.boolean()
  })
  .strict();

const readerEventBaseSchema = {
  eventId: canonicalIdSchema,
  contentId: canonicalIdSchema,
  contentVersionId: canonicalIdSchema,
  displayLanguage: readerLanguageSchema,
  occurredAt: z.iso.datetime({ offset: true })
} as const;

const annotationEventPropertiesSchema = z
  .object({
    sourceLocation: z.enum(["title", "segment"]),
    sourceSegmentId: canonicalIdSchema.nullable().optional(),
    segmentIndex: z.number().int().nonnegative().nullable().optional(),
    sourceTerm: z.string().trim().min(1).max(80),
    occurrenceIndex: z.number().int().nonnegative()
  })
  .strict()
  .superRefine((properties, context) => {
    const segment = properties.sourceLocation === "segment";
    if (segment !== Boolean(properties.sourceSegmentId)) {
      context.addIssue({
        code: "custom",
        path: ["sourceSegmentId"],
        message: "Segment annotations require a Source Segment"
      });
    }
    if (
      segment !==
      (properties.segmentIndex !== null &&
        properties.segmentIndex !== undefined)
    ) {
      context.addIssue({
        code: "custom",
        path: ["segmentIndex"],
        message: "Segment annotations require a segment index"
      });
    }
  });

export const readerEventRequestSchema = z.discriminatedUnion("eventName", [
  z
    .object({
      ...readerEventBaseSchema,
      eventName: z.literal("content_opened"),
      properties: z.object({}).strict()
    })
    .strict(),
  z
    .object({
      ...readerEventBaseSchema,
      eventName: z.literal("original_segment_revealed"),
      properties: z
        .object({ segmentIndex: z.number().int().nonnegative() })
        .strict()
    })
    .strict(),
  z
    .object({
      ...readerEventBaseSchema,
      eventName: z.enum(["term_annotation_opened", "term_annotation_hidden"]),
      properties: annotationEventPropertiesSchema
    })
    .strict()
]);

export const readerEventResponseSchema = z
  .object({ accepted: z.literal(true) })
  .strict();

export type ReaderContentResponse = z.infer<typeof readerContentResponseSchema>;
export type ReaderBookmarkMutationResponse = z.infer<
  typeof readerBookmarkMutationResponseSchema
>;
export type ReaderReactionMutationResponse = z.infer<
  typeof readerReactionMutationResponseSchema
>;
export type ReaderProgressMutationRequest = z.infer<
  typeof readerProgressMutationRequestSchema
>;
export type ReaderProgressMutationResponse = z.infer<
  typeof readerProgressMutationResponseSchema
>;
export type ReaderEventRequest = z.infer<typeof readerEventRequestSchema>;
